<template>
  <div class="purchases-shell">
    <!-- HERO -->
    <section class="purchase-hero">
      <div class="hero-copy-wrap">
        <p class="eyebrow">LotLedger Wine Bar</p>
        <h1>Purchases</h1>
        <p class="hero-copy">
          Manage distributor orders, invoices, receiving, and inventory flow from one dark little control room.
        </p>
      </div>

      <button @click="openAddPopup" class="primary-action">
        + New Purchase
      </button>
    </section>

    <!-- TOOLBAR -->
    <section class="purchase-toolbar">
      <div class="search-wrap">
        <span class="search-icon">⌕</span>
        <input
          type="text"
          v-model="filters.search"
          placeholder="Search distributor, invoice, item..."
          @input="fetchAuctions"
        />
      </div>

      <select v-model="filters.platform" class="toolbar-select">
        <option value="">All Distributors</option>
        <option
          v-for="platform in platforms || []"
          :key="platform"
          :value="platform"
        >
          {{ platform }}
        </option>
      </select>

      <select v-model="filters.received_status" class="toolbar-select">
        <option value="">All Statuses</option>
        <option value="Y">Received</option>
        <option value="N">Open</option>
      </select>
    </section>

    <!-- PURCHASE CARDS -->
    <section v-if="filteredAuctions.length > 0" class="purchase-grid">
      <article
        v-for="purchase in sortedAuctions"
        :key="purchase.auction_id"
        class="purchase-card"
        :class="{ received: purchase.received_status === 'Y' }"
      >
        <div class="purchase-card-main" @click="toggleExpand(purchase.auction_id)">
          <div class="purchase-title-row">
            <div>
              <p class="label">Distributor / Wholesaler</p>
              <h2>{{ purchase.platform || 'Unknown Distributor' }}</h2>
            </div>

            <span
              class="status-pill"
              :class="purchase.received_status === 'Y' ? 'status-received' : 'status-open'"
            >
              {{ purchase.received_status === 'Y' ? 'Received' : 'Open' }}
            </span>
          </div>

          <div class="purchase-metrics">
            <div>
              <span>Purchase #</span>
              <strong>{{ purchase.auction_id }}</strong>
            </div>

            <div>
              <span>Order Date</span>
              <strong>{{ formatDate(purchase.auction_date) }}</strong>
            </div>

            <div>
              <span>Total Cost</span>
              <strong>
                ${{ new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(purchase?.total_cost || 0) }}
              </strong>
            </div>

            <div>
              <span>Items</span>
              <strong>{{ auctionLines?.[purchase.auction_id]?.length || 0 }}</strong>
            </div>
          </div>

          <div class="tracking-row" v-if="!useItemTracking && purchase.tracking_number">
            <button
              class="text-link"
              @click.stop.prevent="openTrackingLink(purchase.tracking_number, purchase.carrier)"
            >
              Track {{ purchase.carrier || 'shipment' }} · {{ purchase.tracking_number }}
            </button>
          </div>
        </div>

        <div class="card-actions">
          <button @click="toggleExpand(purchase.auction_id)" class="ghost-action">
            {{ expandedAuctions.includes(purchase.auction_id) ? 'Close' : 'Open' }}
          </button>

          <button @click="editAuction(purchase)" class="ghost-action">
            Edit
          </button>

          <button
            v-if="purchase.received_status !== 'Y'"
            @click="receiveAll(purchase)"
            class="receive-action"
          >
            Receive
          </button>

          <button
            v-if="purchase.received_status === 'Y'"
            @click="unReceiveAuction(purchase)"
            class="warning-action"
          >
            Unreceive
          </button>

          <button @click="openAddLinePopup(purchase)" class="ghost-action">
            Add Item
          </button>

          <button @click="deleteAuction(purchase.auction_id)" class="danger-action">
            Delete
          </button>
        </div>

        <!-- EXPANDED LINE ITEMS -->
        <transition name="expand">
          <div
            v-if="expandedAuctions.includes(purchase.auction_id)"
            class="purchase-lines"
          >
            <div class="line-header">
              <div>
                <p class="label">Purchase Items</p>
                <h3>Items on this order</h3>
              </div>

              <label class="tracking-toggle">
                <input type="checkbox" v-model="useItemTracking" />
                Per-item tracking
              </label>
            </div>

            <div
              v-if="auctionLines?.[purchase.auction_id]?.length"
              class="line-list"
            >
              <div
                v-for="line in auctionLines[purchase.auction_id]"
                :key="line?.auction_detail_id"
                class="line-card"
              >
                <div class="line-main">
                  <h4>{{ line?.item_name }}</h4>
                  <p>{{ line?.description || 'No description' }}</p>

                  <button
                    v-if="useItemTracking && line?.tracking_no_line && line?.carrier_line"
                    class="text-link compact"
                    @click.prevent="openTrackingLink(line?.tracking_no_line, line?.carrier_line)"
                  >
                    Track {{ line?.carrier_line }} · {{ line?.tracking_no_line }}
                  </button>
                </div>

                <div class="line-meta">
                  <span>Qty {{ line?.quantity }}</span>
                  <span>Invoice ${{ money(line?.unit_cost) }}</span>
                  <span>Landed ${{ money(line?.landed_unit_cost) }}</span>
                </div>

                <span
                  class="mini-pill"
                  :class="line?.received_flag === 'Y' ? 'status-received' : 'status-open'"
                >
                  {{ line?.received_flag === 'Y' ? 'Received' : 'Open' }}
                </span>

                <div class="line-actions">
                  <button
                    v-if="line?.received_flag === 'N'"
                    @click="openReceivePopup(line)"
                  >
                    Receive
                  </button>

                  <button
                    v-if="line?.received_flag === 'Y'"
                    @click="openUnreceivePopup(line)"
                  >
                    Unreceive
                  </button>

                  <button @click="editLine(line)">
                    Edit
                  </button>

                  <button @click="confirmDeleteLine(line)" class="danger-text">
                    Delete
                  </button>
                </div>
              </div>
            </div>

            <div v-else class="empty-lines">
              <h4>No items yet</h4>
              <p>Add bottles, cases, kegs, supplies, or anything else from this distributor order.</p>
              <button @click="openAddLinePopup(purchase)" class="primary-action small">
                + Add Item
              </button>
            </div>
          </div>
        </transition>
      </article>
    </section>

    <!-- EMPTY STATE -->
    <section v-else class="empty-state">
      <p class="eyebrow">No Purchases</p>
      <h2>Nothing in the cellar yet.</h2>
      <p>Add your first distributor purchase to start tracking inventory.</p>
      <button @click="openAddPopup" class="primary-action">
        + New Purchase
      </button>
    </section>
  </div>

  <!-- ADD PURCHASE MODAL -->
  <div v-show="showAuctionPopup" class="modal-overlay">
    <div class="purchase-modal wide-modal">
      <div class="modal-topline"></div>

      <header class="modal-header">
        <div>
          <p class="eyebrow">New Distributor Order</p>
          <h2>Add Purchase</h2>
          <p>Create the purchase header, then add the items from the invoice.</p>
        </div>

        <button type="button" @click="showAuctionPopup = false" class="icon-close">
          ×
        </button>
      </header>

      <form @submit.prevent="createAuction" class="modal-body">
        <section class="form-panel">
          <div class="panel-heading">
            <h3>Distributor Details</h3>
            <p>Who the order came from and how it is being tracked.</p>
          </div>

          <div class="form-grid">
            <label>
              <span>Distributor / Wholesaler</span>
              <input
                type="text"
                v-model="newAuction.platform"
                @input="filterPlatformSuggestions"
                list="platformSuggestions"
                required
                placeholder="Example: Breakthru, RNDC, local winery..."
              />
              <datalist id="platformSuggestions">
                <option
                  v-for="platform in platforms"
                  :key="platform"
                  :value="platform"
                />
              </datalist>
            </label>

            <label>
              <span>Order Date</span>
              <input type="date" v-model="newAuction.auction_date" required />
            </label>

            <label>
              <span>PO / Lot Number</span>
              <input type="text" v-model="newAuction.lot_number" placeholder="Optional" />
            </label>

            <label>
              <span>Invoice #</span>
              <input type="text" v-model="newAuction.invoice_number" placeholder="Invoice number" />
            </label>

            <label>
              <span>Tracking #</span>
              <input type="text" v-model="newAuction.tracking_number" placeholder="Optional" />
            </label>

            <label>
              <span>Carrier</span>
              <select v-model="newAuction.carrier">
                <option value="">Select Carrier</option>
                <option
                  v-for="carrier in carriers"
                  :key="carrier"
                  :value="carrier"
                >
                  {{ carrier }}
                </option>
              </select>
            </label>
          </div>
        </section>

        <section class="form-panel">
          <div class="panel-heading">
            <h3>Cost Breakdown</h3>
            <p>These values allocate down to each purchase item.</p>
          </div>

          <div class="form-grid four-col">
            

            <label>
              <span>Fees / Premium</span>
              <input
                type="number"
                v-model.number="newAuction.buyer_premium"
                step="0.01"
                required
              />
            </label>

            <label>
              <span>Total Taxes</span>
              <input
                type="number"
                v-model.number="newAuction.total_taxes"
                step="0.01"
                required
              />
            </label>

            <label>
              <span>Total Freight</span>
              <input
                type="number"
                v-model.number="newAuction.total_freight"
                step="0.01"
                required
              />
            </label>
          </div>
        </section>

        <section class="form-panel">
          <div class="panel-heading split-heading">
            <div>
              <h3>Purchase Items</h3>
              <p>Add bottles, cases, kegs, food items, supplies, or merch.</p>
            </div>

            <button type="button" @click="addLineItem" class="secondary-action">
              + Row
            </button>
          </div>

          <div class="touch-table">
            <div class="touch-table-head">
              <span>Item</span>
              <span>Item No</span>
              <span>Qty</span>
              <span>Unit Cost</span>
              <span>Description</span>
              <span></span>
            </div>

            <div
              v-for="(item, index) in newAuction.items"
              :key="index"
              class="touch-table-row"
            >
              <div>
                <input
                  type="text"
                  v-model="item.item_name"
                  @input="fetchItemSuggestions(index)"
                  list="itemSuggestions"
                  @change="autoFillItemDetails(index)"
                  required
                  :ref="'itemNameInput-' + index"
                  placeholder="Start typing item name..."
                />

                <datalist id="itemSuggestions">
                  <option
                    v-for="suggestedItem in itemSuggestions"
                    :key="suggestedItem.item_no"
                    :value="suggestedItem.item_name"
                  />
                </datalist>
              </div>

              <input
                type="text"
                v-model="item.item_no"
                disabled
                class="disabled-field"
              />

              <input
                type="number"
                v-model="item.quantity"
                required
                min="1"
                @keydown.tab.prevent="addNewRow(index)"
              />

              <input
                type="number"
                v-model.number="item.unit_cost"
                required
                min="0"
                step="0.01"
                placeholder="0.00"
              />

              <input
                type="text"
                v-model="item.description"
                :disabled="!item.item_no"
                class="disabled-field"
                placeholder="Auto-filled"
              />

              <button
                type="button"
                class="row-delete"
                @click="removeLineItem(index)"
                title="Remove row"
              >
                ×
              </button>
            </div>
          </div>
        </section>

        <footer class="modal-actions">
          <button type="button" @click="showAuctionPopup = false" class="cancel-action">
            Cancel
          </button>

          <button type="submit" class="primary-action">
            Save Purchase
          </button>
        </footer>
      </form>
    </div>
  </div>

  <!-- ADD NEW ITEM MODAL -->
  <div v-if="showNewItemModal" class="modal-overlay nested">
    <div class="purchase-modal small-modal">
      <div class="modal-topline gold"></div>

      <header class="modal-header">
        <div>
          <p class="eyebrow">New Inventory Item</p>
          <h2>Add New Item</h2>
          <p>This item is not in your item list yet.</p>
        </div>

        <button type="button" @click="showNewItemModal = false" class="icon-close">
          ×
        </button>
      </header>

      <div class="modal-body">
        <section class="form-panel">
          <label>
            <span>Item Name</span>
            <input v-model="newItemDetails.item_name" type="text" disabled />
          </label>

          <label>
            <span>Description</span>
            <input
              v-model="newItemDetails.description"
              type="text"
              ref="newItemDescriptionInput"
              placeholder="Example: Willamette Pinot Noir 750ml"
            />
          </label>

          <label>
            <span>Wine Type</span>
            <select v-model="newItemDetails.wine_type" required>
              <option value="" disabled>Select Wine Type</option>
              <option value="Red Wine">Red Wine</option>
              <option value="White">White</option>
              <option value="Rosé">Rosé</option>
              <option value="Sparkling">Sparkling</option>
              <option value="Rosé Sparkling">Rosé Sparkling</option>
              <option value="Beer">Beer</option>
              <option value="Spirits">Spirits</option>
              <option value="Food">Food</option>
              <option value="Supply">Supply</option>
              <option value="Other">Other</option>
            </select>
          </label>

          <label>
            <span>Class</span>
            <select v-model="newItemDetails.service_class" required>
              <option value="" disabled>Select Class</option>
              <option value="pour">Pour</option>
              <option value="reserve">Reserve</option>
              <option value="bottle">Bottle</option>
              <option value="plate">Plate</option>
              <option value="supply">Supply</option>
              <option value="merch">Merch</option>
              <option value="other">Other</option>
            </select>
          </label>

          <label>
            <span>Vintage</span>
            <input
              v-model="newItemDetails.vintage"
              type="text"
              placeholder="2021, NV..."
            />
          </label>

          <label>
            <span>Bottle Size</span>
            <input
              v-model="newItemDetails.bottle_size"
              type="text"
              placeholder="750ml"
            />
          </label>

          <label>
            <span>Producer / Chateau</span>
            <input
              v-model="newItemDetails.chateau"
              type="text"
              placeholder="Producer, winery, chateau..."
            />
          </label>

          <label>
            <span>Region</span>
            <input
              v-model="newItemDetails.region"
              type="text"
              placeholder="Bordeaux, Willamette, Provence..."
            />
          </label>

          <label>
            <span>Country</span>
            <input
              v-model="newItemDetails.country"
              type="text"
              placeholder="France, USA, Italy..."
            />
          </label>
        </section>

        <footer class="modal-actions">
          <button type="button" @click="showNewItemModal = false" class="cancel-action">
            Cancel
          </button>

          <button type="button" @click="saveNewItem" class="primary-action">
            Save Item
          </button>
        </footer>
      </div>
    </div>
  </div>

  <!-- EDIT PURCHASE MODAL -->
  <div v-if="showEditPopup" class="modal-overlay">
    <div class="purchase-modal">
      <div class="modal-topline"></div>

      <header class="modal-header">
        <div>
          <p class="eyebrow">Edit Distributor Order</p>
          <h2>Edit Purchase</h2>
          <p>Update invoice, tracking, and cost details before the order is received.</p>
        </div>

        <button type="button" @click="showEditPopup = false" class="icon-close">
          ×
        </button>
      </header>

      <form @submit.prevent="updateAuction" class="modal-body">
        <section class="form-panel">
          <div class="form-grid">
            <label>
              <span>Distributor / Wholesaler</span>
              <input
                type="text"
                v-model="editAuctionData.platform"
                @input="filterPlatformSuggestions"
                list="platformSuggestions"
                required
              />
            </label>

            <label>
              <span>Order Date</span>
              <input type="date" v-model="editAuctionData.auction_date" required />
            </label>

            <label>
              <span>PO / Lot Number</span>
              <input type="text" v-model="editAuctionData.lot_number" required />
            </label>

            <label>
              <span>Invoice #</span>
              <input type="text" v-model="editAuctionData.invoice_number" />
            </label>

            <label>
              <span>Tracking #</span>
              <input type="text" v-model="editAuctionData.tracking_number" />
            </label>

            <label>
              <span>Carrier</span>
              <select v-model="editAuctionData.carrier">
                <option value="">Select Carrier</option>
                <option
                  v-for="carrier in carriers"
                  :key="carrier"
                  :value="carrier"
                >
                  {{ carrier }}
                </option>
              </select>
            </label>
          </div>
        </section>

        <section class="form-panel">
          <div class="panel-heading">
            <h3>Cost Breakdown</h3>
            <p>Total cost updates in real time.</p>
          </div>

          <div class="form-grid">
            <label>
              <span>Product Cost</span>
              <input
                type="number"
                v-model.number="editAuctionData.winning_bid"
                step="0.01"
                required
              />
            </label>

            <label>
              <span>Fees / Premium</span>
              <input
                type="number"
                v-model.number="editAuctionData.buyer_premium"
                step="0.01"
                required
              />
            </label>

            <label>
              <span>Total Taxes</span>
              <input
                type="number"
                v-model.number="editAuctionData.total_taxes"
                step="0.01"
                required
              />
            </label>

            <label>
              <span>Total Freight</span>
              <input
                type="number"
                v-model.number="editAuctionData.total_freight"
                step="0.01"
                required
              />
            </label>

            <label class="total-field">
              <span>Total Cost</span>
              <input
                type="number"
                :value="realTimeTotalCost"
                disabled
                class="disabled-field"
              />
            </label>
          </div>
        </section>

        <footer class="modal-actions">
          <button type="button" @click="showEditPopup = false" class="cancel-action">
            Cancel
          </button>

          <button type="submit" class="primary-action">
            Update Purchase
          </button>
        </footer>
      </form>
    </div>
  </div>

  <!-- ADD LINE MODAL -->
  <div v-if="showAddLinePopup" class="modal-overlay">
    <div class="purchase-modal small-modal">
      <div class="modal-topline gold"></div>

      <header class="modal-header">
        <div>
          <p class="eyebrow">Add Purchase Item</p>
          <h2>Add Item</h2>
          <p>Add another item to this distributor order.</p>
        </div>

        <button type="button" @click="closeAddLinePopup" class="icon-close">
          ×
        </button>
      </header>

      <div class="modal-body">
        <section class="form-panel">
          <label>
            <span>Item Name</span>
            <input
              v-model="newLine.item_name"
              type="text"
              @input="fetchItemSuggestions()"
              @change="autoFillItemDetails()"
              list="itemSuggestions"
              required
              ref="itemNameInput"
              placeholder="Start typing item name..."
            />

            <datalist id="itemSuggestions">
              <option
                v-for="item in filteredItems"
                :key="item.item_no"
                :value="item.item_name"
              />
            </datalist>
          </label>

          <label>
            <span>Item No</span>
            <input
              v-model="newLine.item_no"
              type="text"
              disabled
              class="disabled-field"
            />
          </label>

          <label>
            <span>Description</span>
            <input
              v-model="newLine.description"
              type="text"
              disabled
              class="disabled-field"
            />
          </label>

          <label>
            <span>Quantity</span>
            <input
              v-model.number="newLine.quantity"
              type="number"
              min="1"
              required
              @input="calculateTotalCost"
            />
          </label>

          <label>
            <span>Unit Cost</span>
            <input
              v-model.number="newLine.unit_cost"
              type="number"
              min="0"
              step="0.01"
              required
              @input="calculateTotalCost"
            />
          </label>

        </section>

        <footer class="modal-actions">
          <button type="button" @click="closeAddLinePopup" class="cancel-action">
            Cancel
          </button>

          <button type="button" @click="addAuctionLine" class="primary-action">
            Add Item
          </button>
        </footer>
      </div>
    </div>
  </div>

  <!-- EDIT LINE MODAL -->
  <div v-if="showEditLinePopup" class="modal-overlay">
    <div class="purchase-modal">
      <div class="modal-topline gold"></div>

      <header class="modal-header">
        <div>
          <p class="eyebrow">Edit Purchase Item</p>
          <h2>Edit Line Item</h2>
          <p>Adjust quantity and optional item-level tracking.</p>
        </div>

        <button type="button" @click="showEditLinePopup = false" class="icon-close">
          ×
        </button>
      </header>

      <form @submit.prevent="updateAuctionLine" class="modal-body">
        <section class="form-panel">
          <div class="form-grid">
            <label>
              <span>Item Name</span>
              <input type="text" v-model="editLineData.item_name" disabled />
            </label>

            <label>
              <span>Item No</span>
              <input type="text" v-model="editLineData.item_no" disabled />
            </label>

            <label>
              <span>Quantity</span>
              <input
                type="number"
                v-model.number="editLineData.quantity"
                step="1"
                min="1"
                required
              />
            </label>

            <label>
              <span>Tracking No</span>
              <input
                type="text"
                v-model="editLineData.tracking_no_line"
                placeholder="Optional"
              />
            </label>

            <label>
              <span>Carrier</span>
              <select v-model="editLineData.carrier_line">
                <option value="">Select Carrier</option>
                <option
                  v-for="carrier in carriers"
                  :key="carrier"
                  :value="carrier"
                >
                  {{ carrier }}
                </option>
              </select>
            </label>
          </div>
        </section>

        <section class="form-panel">
          <div class="panel-heading">
            <h3>Allocated Costs</h3>
            <p>These values are calculated from the purchase total.</p>
          </div>

          <div class="form-grid four-col">
            <label>
              <span>Allocated Cost</span>
              <input type="number" :value="updatedAllocatedCost" disabled />
            </label>

            <label>
              <span>Allocated Premium</span>
              <input type="number" :value="updatedAllocatedPremium" disabled />
            </label>

            <label>
              <span>Allocated Tax</span>
              <input type="number" :value="updatedAllocatedTax" disabled />
            </label>

            <label>
              <span>Allocated Freight</span>
              <input type="number" :value="updatedAllocatedFreight" disabled />
            </label>
          </div>
        </section>

        <footer class="modal-actions">
          <button type="button" @click="showEditLinePopup = false" class="cancel-action">
            Cancel
          </button>

          <button type="submit" class="primary-action">
            Update Item
          </button>
        </footer>
      </form>
    </div>
  </div>

  <!-- RECEIVE PURCHASE MODAL -->
  <div v-if="showReceivePopup" class="modal-overlay">
    <div class="purchase-modal small-modal">
      <div class="modal-topline green"></div>

      <header class="modal-header">
        <div>
          <p class="eyebrow">Receive Inventory</p>
          <h2>Receive Purchase?</h2>
          <p>This will receive all items on this purchase and update inventory.</p>
        </div>

        <button type="button" @click="showReceivePopup = false" class="icon-close">
          ×
        </button>
      </header>

      <div class="modal-body">
        <section class="form-panel">
          <label>
            <span>Receipt Date</span>
            <input
              type="date"
              v-model="receiptDate"
            />
          </label>
        </section>

        <footer class="modal-actions">
          <button type="button" @click="showReceivePopup = false" class="cancel-action">
            Cancel
          </button>

          <button type="button" @click="confirmReceiveAuction" class="receive-action modal-button">
            Yes, Receive All
          </button>
        </footer>
      </div>
    </div>
  </div>

  <!-- RECEIVE LINE MODAL -->
  <div v-if="showReceiveLinePopup" class="modal-overlay">
    <div class="purchase-modal small-modal">
      <div class="modal-topline green"></div>

      <header class="modal-header">
        <div>
          <p class="eyebrow">Receive Item</p>
          <h2>Receive Line?</h2>
          <p>
            Receive
            <strong>{{ lineToReceive?.item_name }}</strong>
            <span v-if="lineToReceive?.quantity">
              · Qty {{ lineToReceive.quantity }}
            </span>
          </p>
        </div>

        <button type="button" @click="showReceiveLinePopup = false" class="icon-close">
          ×
        </button>
      </header>

      <div class="modal-body">
        <section class="form-panel">
          <label>
            <span>Receipt Date</span>
            <input
              type="date"
              v-model="lineToReceive.receipt_date"
            />
          </label>
        </section>

        <footer class="modal-actions">
          <button type="button" @click="showReceiveLinePopup = false" class="cancel-action">
            Cancel
          </button>

          <button type="button" @click="confirmReceiveLine" class="receive-action modal-button">
            Receive Item
          </button>
        </footer>
      </div>
    </div>
  </div>

  <!-- UNRECEIVE LINE MODAL -->
  <div v-if="showUnreceiveLinePopup" class="modal-overlay">
    <div class="purchase-modal small-modal">
      <div class="modal-topline amber"></div>

      <header class="modal-header">
        <div>
          <p class="eyebrow">Reverse Receipt</p>
          <h2>Unreceive Item?</h2>
          <p>
            This will reverse inventory for
            <strong>{{ lineToReceive?.item_name }}</strong>.
          </p>
        </div>

        <button type="button" @click="showUnreceiveLinePopup = false" class="icon-close">
          ×
        </button>
      </header>

      <div class="modal-body">
        <footer class="modal-actions">
          <button type="button" @click="showUnreceiveLinePopup = false" class="cancel-action">
            Cancel
          </button>

          <button type="button" @click="confirmUnreceiveLine" class="warning-action modal-button">
            Yes, Unreceive
          </button>
        </footer>
      </div>
    </div>
  </div>

  <!-- DELETE LINE MODAL -->
  <div v-if="showDeletePopup" class="modal-overlay">
    <div class="purchase-modal small-modal">
      <div class="modal-topline red"></div>

      <header class="modal-header">
        <div>
          <p class="eyebrow">Delete Item</p>
          <h2>Delete this line?</h2>
          <p>This cannot be undone.</p>
        </div>

        <button type="button" @click="showDeletePopup = false" class="icon-close">
          ×
        </button>
      </header>

      <div class="modal-body">
        <footer class="modal-actions">
          <button type="button" @click="showDeletePopup = false" class="cancel-action">
            Cancel
          </button>

          <button type="button" @click="deleteLineConfirmed" class="danger-action modal-button">
            Delete
          </button>
        </footer>
      </div>
    </div>
  </div>

  <!-- RECEIVED WARNING MODAL -->
  <div v-if="showReceivedWarning" class="modal-overlay">
    <div class="purchase-modal small-modal">
      <div class="modal-topline amber"></div>

      <header class="modal-header">
        <div>
          <p class="eyebrow">Locked Item</p>
          <h2>Already Received</h2>
          <p>This line has been received. Please unreceive it before deleting.</p>
        </div>

        <button type="button" @click="showReceivedWarning = false" class="icon-close">
          ×
        </button>
      </header>

      <div class="modal-body">
        <footer class="modal-actions">
          <button type="button" @click="showReceivedWarning = false" class="primary-action">
            OK
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  data() {
    return {
      totalAuctionQuantity: 0,  // ✅ Now Vue tracks changes properly
      filters: { search: '', platform: '', received_status: '' },
      platforms: [], // ✅ Ensure this exists!
      filteredPlatforms: [], // ✅ Ensure this exists!
      expandedAuctions: [],
      carriers: [],
      auctions: [],
      showReceivePopup: false,
      auctionToReceive: null, // Stores selected auction before confirmation
      receiptDate: new Date().toISOString().split('T')[0],
      auctionLines: {},
      useItemTracking: false,
      showEditPopup: false,
      showEditLinePopup: false,
      showAuctionPopup: false,
      showReceiveLinePopup: false,
      showUnreceiveLinePopup: false,
      lineToReceive: null,  // Stores the selected line item for receiving/unreceiving
      showDeletePopup: false,   // ✅ Controls Delete Confirmation Popup
      showReceivedWarning: false, // ✅ Controls Warning Popup
      selectedLineToDelete: null, // ✅ Stores the selected line for deletion
      

      editAuctionData: {
        auction_id: null,
        platform: "",
        auction_date: "",
        lot_number: "",
        invoice_number: "",
        tracking_number: "",
        carrier: "",
        winning_bid: 0,
        buyer_premium: 0,
        total_taxes: 0,
        total_freight: 0,
        total_cost: 0
      },
      

      editLineData: {
        auction_detail_id: null,
        item_name: "",
        tracking_no_line: "",
        carrier_line: "",
        item_no: "",
        quantity: 0,

        // invoice unit cost entered by user
        unit_cost: 0,

        // backend-calculated line totals / allocations
        allocated_cost: 0,
        allocated_premium: 0,
        allocated_taxes: 0,
        allocated_freight: 0
      },

      showAddLinePopup: false,
        auctionToEdit: null, // Holds auction ID when adding a line
        received_status: false, // ✅ Track if the auction has received items
        newLine: {
          item_no: "",
          item_name: "",
          description: "",
          quantity: null,
          unit_cost: 0
        },
        totalCost: 0,
        

        newAuction: {
            platform: '',
            auction_date: '',
            lot_number: '',
            invoice_number: '',
            tracking_number: '',
            carrier: '',
            winning_bid: 0,
            buyer_premium: 0,
            total_taxes: 0,
            total_freight: 0,
            items: [
                { 
                    item_name: '', 
                    item_no: '', 
                    quantity: null,
                    unit_cost: 0,
                    description: ''                    
                }
            ]
        },

      

      itemSuggestions: [], // Stores search suggestions      
      showNewItemModal: false,  // ✅ Controls visibility of the "Add New Item" modal
        currentItemIndex: null,   // ✅ Stores which auction row we're working with
        newItemDetails: {
            item_name: "",
            description: "",
            wine_type: "",
            service_class: "",
            vintage: "",
            bottle_size: "750ml",
            chateau: "",
            region: "",
            country: "",
            category_id: ""
        }
    };
  },

  computed: {
  realTimeTotalCost() {
    return (
      Number(this.editAuctionData.winning_bid || 0) +
      Number(this.editAuctionData.buyer_premium || 0) +
      Number(this.editAuctionData.total_taxes || 0) +
      Number(this.editAuctionData.total_freight || 0)
    ).toFixed(2);
  },

  // ✅ Allocated Premium: Start with database value, update in real-time
  updatedAllocatedPremium() {
    if (!this.editLineData.quantity || !this.totalAuctionQuantity) {
      return this.editLineData.allocated_premium || "0.00";  // ✅ Use DB value first
    }
    return ((this.editLineData.quantity / this.totalAuctionQuantity) * (this.auctionHeaderData?.buyer_premium || 0)).toFixed(2);
  },

  // ✅ Allocated Tax
  updatedAllocatedTax() {
    if (!this.editLineData.quantity || !this.totalAuctionQuantity) {
      return this.editLineData.allocated_taxes || "0.00";  // ✅ Use DB value first
    }
    return ((this.editLineData.quantity / this.totalAuctionQuantity) * (this.auctionHeaderData?.total_taxes || 0)).toFixed(2);
  },

  // ✅ Allocated Freight
  updatedAllocatedFreight() {
    if (!this.editLineData.quantity || !this.totalAuctionQuantity) {
      return this.editLineData.allocated_freight || "0.00";
    }
    return ((this.editLineData.quantity / this.totalAuctionQuantity) * (this.auctionHeaderData?.total_freight || 0)).toFixed(2);
  },

  // ✅ Allocated Cost
  updatedAllocatedCost() {
    if (!this.editLineData.quantity || !this.totalAuctionQuantity) {
      return this.editLineData.allocated_cost || "0.00";  // ✅ Use DB value first
    }
    return ((this.editLineData.quantity / this.totalAuctionQuantity) * (this.auctionHeaderData?.winning_bid || 0)).toFixed(2);
  },

  sortedAuctions() {
  return this.filteredAuctions.slice().sort((a, b) => a.auction_id - b.auction_id);
},


  filteredItems() {
        if (!this.newLine.item_name) return this.itemSuggestions;
        return this.itemSuggestions.filter(item =>
            item.item_name.toLowerCase().includes(this.newLine.item_name.toLowerCase())
        );
    },


  filteredAuctions() {
    console.log("🔎 Debugging filteredAuctions...");
    
    if (!Array.isArray(this.auctions) || this.auctions.length === 0) {
        console.log("🚨 No auctions available, returning empty array.");
        return []; // ✅ Prevents Vue errors
    }

    return this.auctions.filter(a => {
        const passesHeaderSearch = !this.filters.search || 
            (a.platform && a.platform.toLowerCase().includes(this.filters.search.toLowerCase()));

        const passesLineSearch = !this.filters.search || 
            (this.auctionLines?.[a.auction_id] && 
             this.auctionLines[a.auction_id]?.some(line =>
                (line.item_name && line.item_name.toLowerCase().includes(this.filters.search.toLowerCase())) ||
                (line.description && line.description.toLowerCase().includes(this.filters.search.toLowerCase()))
            ));

        const passesSearch = passesHeaderSearch || passesLineSearch;
        const passesPlatform = !this.filters.platform || a.platform === this.filters.platform;
        const passesStatus = !this.filters.received_status || String(a.received_status) === this.filters.received_status;

        console.log("👉 Auction:", a.platform, "| Search Match:", passesSearch, "| Platform Match:", passesPlatform, "| Status Match:", passesStatus);

        return passesSearch && passesPlatform && passesStatus;
    });
  }
},


  mounted() {
    console.log("🚀 Component Mounted!");
    this.fetchAuctions();
    this.fetchPlatforms(); // 🔥 Fetch platforms on page load
    this.fetchCarriers();
  },

 methods: {
  async fetchAuctions() {
  try {
    console.log("🟢 fetchAuctions() is being called");

    let response;

    if (this.filters.search) {
      console.log("🔍 Fetching search results for:", this.filters.search);
      response = await api.get(`/auctions/search?query=${encodeURIComponent(this.filters.search)}`);
      console.log("📥 Search Response:", response.data);
    } else {
      console.log("📥 Fetching all auctions");
      response = await api.get('/auctions');
      console.log("📥 All Auctions Response:", response.data);
    }

    // ✅ Ensure received_status is always "Y" or "N"
    this.auctions = response.data.map(auction => ({
      ...auction,
      received_status: auction.received_status === 'Y' ? 'Y' : 'N' // ✅ Ensure Vue recognizes it
    }));

    console.log("🔄 Updated Auctions After Processing:", this.auctions);

    // 🟢 Fetch missing auction lines in parallel
    await Promise.all(this.auctions.map(async (auction) => {
      if (!this.auctionLines[auction.auction_id]) {
        await this.fetchAuctionLines(auction.auction_id);
      }
    }));

  } catch (error) {
    console.error('❌ Error fetching auctions:', error);
  }
},

invoiceUnitCost(line) {
  const qty = Number(line.quantity || 0);
  if (!qty) return 0;
  return Number(line.allocated_cost || 0) / qty;
},

money(value) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(value || 0));
},


focusNextField(index) {
    console.log(`🔍 Tabbing from description field at index ${index}`);

    /// ✅ Only update description if the item has an `item_no` (meaning it exists)
    if (this.newAuction.items[index].item_no) {
        this.updateItemDescription(index);
    }

    // Find the next input field
    const nextRow = this.$refs[`itemRow-${index + 1}`]?.[0];

    if (nextRow) {
        console.log("➡ Moving to the next row...");
        nextRow.focus();
    } else {
        console.log("➕ Adding a new row...");
        this.addNewRow(index);
    }
},

async fetchPlatforms() {
  try {
    console.log("Fetching platforms...");
    const response = await api.get('/auction_houses');

    if (!response.data || response.data.length === 0) {
      console.warn("⚠️ No platforms returned from API!");
      this.platforms = [];
    } else {
      this.platforms = response.data.map(house => house.name); // Extract names
    }

    console.log("✅ Platforms loaded:", this.platforms);
  } catch (error) {
    console.error("❌ Error fetching platforms:", error);
    this.platforms = []; // Avoid Vue errors by ensuring it's an array
  }
},

async fetchCarriers() {
  try {
    console.log("Fetching carriers...");
    const response = await api.get('/carriers'); // No need for response.json()

    if (!response.data || response.data.length === 0) {
      console.warn("⚠️ No carriers returned from API!");
      this.carriers = [];
    } else {
      this.carriers = response.data.map(c => c.carrier_name); // Extract carrier names
    }

    console.log("✅ Carriers loaded:", this.carriers);
  } catch (error) {
    console.error("❌ Error fetching carriers:", error);
    this.carriers = []; // Ensure it's always an array to prevent Vue errors
  }
},



// ✅ Platform Autocomplete Logic
filterPlatformSuggestions() {
    if (!this.newAuction.platform) {
        this.filteredPlatforms = [];
        return;
    }

    const search = this.newAuction.platform.toLowerCase();
    this.filteredPlatforms = this.platforms.filter(platform =>
        platform.toLowerCase().includes(search)
    );
},

selectPlatform(platform) {
    this.newAuction.platform = platform; // Autofill input
    this.filteredPlatforms = []; // Hide suggestions after selection
},

// ✅ Open Receive Popup for Individual Line
openReceivePopup(line) {
    if (!line || !line.auction_detail_id) {
        console.error("❌ ERROR: Missing auction_detail_id in line data!", line);
        return;
    }

    console.log("📦 Receive button clicked for line:", line);

    // Ensure lineToReceive contains all necessary fields
    this.lineToReceive = {
        auction_detail_id: line.auction_detail_id,
        auction_id: line.auction_id,
        item_no: line.item_no,
        item_name: line.item_name,
        quantity: line.quantity,  // Ensure quantity is present
        allocated_cost: line.allocated_cost,
        receipt_date: new Date().toISOString().split('T')[0] // ✅ Default to today’s date (YYYY-MM-DD)
    };

    console.log("📌 lineToReceive now contains:", this.lineToReceive);

    this.showReceiveLinePopup = true;  // ✅ Show the popup
},


    // ✅ Open Unreceive Popup for Individual Line
    openUnreceivePopup(line) {
        if (!line || !line.auction_detail_id) {
            console.error("❌ ERROR: Missing auction_detail_id in line data!", line);
            return;
        }

        console.log("🔄 Unreceive button clicked for line:", line);

        this.lineToReceive = {
            auction_detail_id: line.auction_detail_id,
            auction_id: line.auction_id,
            item_no: line.item_no,
            item_name: line.item_name,
            quantity: line.quantity,  // Ensure quantity is present
            allocated_cost: line.allocated_cost
        };

        console.log("📌 lineToReceive now contains:", this.lineToReceive);

        this.showUnreceiveLinePopup = true;  // ✅ Show the popup
    },

// ✅ Process Receiving an Individual Line
// ✅ Process Receiving an Individual Line
async confirmReceiveLine() {
  if (!this.lineToReceive || !this.lineToReceive.auction_detail_id) {
    console.error("❌ ERROR: auction_detail_id is undefined!", this.lineToReceive);
    return;
  }

  try {
    console.log("📦 Confirming receipt for line:", this.lineToReceive);

    // ✅ Step 1: Check for shipment conflicts before proceeding
    try {
      const conflictCheck = await api.get(`/check-ship-conflicts/${this.lineToReceive.auction_id}`, {
        params: {
          receipt_date: this.lineToReceive.receipt_date
        }
      });

      if (conflictCheck.data.conflicts.length > 0) {
        const details = conflictCheck.data.conflicts
          .filter(c => c.item_no === this.lineToReceive.item_no)
          .map(c => `• Item ${c.item_no} in Sale #${c.sale_id}`)
          .join('\n');

        if (details) {
          alert(`❌ Cannot receive:\nThis line item has already been shipped after the selected receipt date.\n\n${details}`);
          console.warn("🚨 Ship Conflicts Detected:", conflictCheck.data.conflicts);
          return;
        }
      }
    } catch (error) {
      console.error("❌ Error checking for ship conflicts:", error);
      alert("❌ Failed to verify shipment conflicts. Please try again.");
      return;
    }

    // ✅ Step 2: Send receipt_date to backend
    const response = await api.post(`/receive_line/${this.lineToReceive.auction_detail_id}`, {
      receipt_date: this.lineToReceive.receipt_date
    });

    console.log("✅ Line received successfully:", response.data);

    this.showReceiveLinePopup = false; // ✅ Close the popup
    this.fetchAuctionLines(this.lineToReceive.auction_id); // ✅ Refresh auction lines

  } catch (error) {
    console.error("❌ Error receiving line:", error);
  }
},



// ✅ Process Unreceiving an Individual Line
async confirmUnreceiveLine() {
  if (!this.lineToReceive || !this.lineToReceive.auction_detail_id) {
    console.error("❌ ERROR: auction_detail_id is undefined!", this.lineToReceive);
    return;
  }

  try {
    console.log("🔄 Confirming unreceipt for line:", this.lineToReceive);

    // ✅ Step 1: Fetch the receipt date from the backend for this line
    const lineResponse = await api.get(`/auction_line/${this.lineToReceive.auction_detail_id}`);
    const line = lineResponse.data;
    const receiptDate = line.receipt_date;

    if (!receiptDate) {
      alert("❌ Cannot unreceive: Missing receipt date for this line.");
      return;
    }

    // ✅ Step 2: Check for shipment conflicts
    try {
      const conflictCheck = await api.get(`/check-ship-conflicts/${line.auction_id}`, {
        params: {
          receipt_date: receiptDate
        }
      });

      if (conflictCheck.data.conflicts.length > 0) {
        const details = conflictCheck.data.conflicts
          .map(c => `• Item ${c.item_no} in Sale #${c.sale_id}`)
          .join('\n');

        alert(`❌ Cannot unreceive:\nOne or more items have already been shipped after the original receipt date.\n\n${details}`);
        console.warn("🚨 Ship Conflicts Detected:", conflictCheck.data.conflicts);
        return;
      }
    } catch (error) {
      console.error("❌ Error checking for ship conflicts:", error);
      alert("❌ Failed to verify shipment conflicts. Please try again.");
      return;
    }

    // ✅ Step 3: Proceed with unreceiving
    const response = await api.put(`/unreceive_line/${this.lineToReceive.auction_detail_id}`, {
      receipt_date: receiptDate
    });

    console.log("✅ Line unreceived successfully:", response.data);

    this.showUnreceiveLinePopup = false; // ✅ Close the popup
    this.fetchAuctionLines(this.lineToReceive.auction_id); // ✅ Refresh auction lines

  } catch (error) {
    console.error("❌ Error unreceiving line:", error);

    if (error.response) {
      console.log("🔍 Full Axios Error Response:", error.response);
      const errorMessage = error.response.data.error || "Unknown error from server.";
      alert(`❌ Unreceive failed: ${errorMessage}`);
    } else {
      alert("❌ Unreceive failed: No response from server.");
    }
  }
},


receiveAll(auction) {
  console.log("📦 Receive All clicked for auction:", auction.auction_id);

  // ✅ Check if any line items are already received
  if (this.auctionLines[auction.auction_id]?.some(line => line.received_flag === 'Y')) {
    alert("❌ Auction partially received. You may only receive at the line level now.");
    return; // 🚫 Stop execution
  }

  this.auctionToReceive = auction; // ✅ Store the auction for confirmation
  this.showReceivePopup = true; // ✅ Show the confirmation popup
},


async addNewRow(index) {
    const currentItem = this.newAuction.items[index] || {}; 
    const itemName = currentItem.item_name ? currentItem.item_name.trim() : ""; 

    if (itemName === "") {
        console.error("❌ ERROR: Cannot add a new row. Item name is missing.");
        return;
    }

    console.log(`🔍 Checking if "${itemName}" exists before adding a new row...`);

    try {
        const response = await api.get(`/items/search?query=${encodeURIComponent(itemName)}`);
        const foundItems = response.data || [];

        if (foundItems.length === 0) {
            console.error("❌ ERROR: Item does not exist in the database!");
            alert("This item does not exist. Please add it first in the item management section.");
            return;
        }

        console.log(`✅ Item "${itemName}" exists, adding new row...`);

        // ✅ Add the new row with a proper structure
        this.newAuction.items.push({
            item_name: "",
            item_no: "",
            quantity: null,
            unit_cost: 0,
            description: "",
        });

        console.log("✅ New row added successfully!");

        // ✅ Wait for Vue to render the new row before focusing
        await this.$nextTick();

        // ✅ Find the new row's input field correctly
        const newRowIndex = this.newAuction.items.length - 1;
        this.$nextTick(() => {
            const newRowInput = this.$refs[`itemNameInput-${newRowIndex}`]?.[0] || this.$refs[`itemNameInput-${newRowIndex}`];
            if (newRowInput) {
                newRowInput.focus();
                console.log(`🎯 Cursor moved to new row's item_name field (row ${newRowIndex})`);
            } else {
                console.error("❌ Failed to find new row input for focusing.");
            }
        });

    } catch (error) {
        console.error("❌ Error checking item existence:", error);
        alert("Error checking item existence. Please try again.");
    }
},


async confirmReceiveAuction() {
  if (!this.auctionToReceive) return;

  try {
    console.log("📦 Confirming receipt for auction:", this.auctionToReceive);
    console.log("📅 Using receipt date:", this.receiptDate);

    // ✅ Step 1: Check for shipment conflicts before receiving
    const conflictCheck = await api.get(`/check-ship-conflicts/${this.auctionToReceive.auction_id}`, {
      params: {
        receipt_date: this.receiptDate
      }
    });

    if (conflictCheck.data.conflicts.length > 0) {
      const details = conflictCheck.data.conflicts
        .map(c => `• Item ${c.item_no} in Sale #${c.sale_id}`)
        .join('\n');

      alert(`❌ Cannot receive:\nOne or more items in this auction have already been shipped after the selected receipt date.\n\n${details}`);
      console.warn("🚨 Ship Conflicts Detected:", conflictCheck.data.conflicts);
      return;
    }

    // ✅ Step 2: Proceed with receipt
    const receivedCost = this.auctionToReceive.total_cost;

    const response = await api.post(`/receive/${this.auctionToReceive.auction_id}`, {
      received_cost: receivedCost,
      transaction_date: this.receiptDate // ✅ New: user-selected receipt date
    });

    console.log("✅ Receive all response:", response.data);

    // ✅ Update the UI to reflect the received status
    this.auctionToReceive.received_status = true;
    this.showReceivePopup = false;

    // 🚀 Force Vue to reactively update auction lines
    this.auctionLines = { ...this.auctionLines };

    this.fetchAuctions(); // ✅ Refresh auction list

  } catch (error) {
    console.error("❌ Error receiving auction:", error);
    alert("❌ Failed to complete receive. See console for details.");
  }
},



async fetchNextAuctionId() {
    try {
        const response = await api.get('/getNextAuctionId'); // This should be a backend route
        const data = response.data;
        console.log("🔢 Next Auction ID from Backend:", data.next_auction_id);
        return data.next_auction_id || 1; // Default to 1 if NULL
    } catch (error) {
        console.error("❌ Error fetching next auction ID:", error);
        return 1; // Fallback to 1 if API fails
    }
},


 // ✅ Opens the Auction Popup and Focuses on First Input
 async openAddPopup() {
    console.log("➕ Add Auction button clicked!");

    const nextId = await this.fetchNextAuctionId();
    console.log("🔢 Next Auction ID from Backend:", nextId);

    this.newAuction = { 
        auction_id: nextId,
        platform: '', 
        auction_date: '', 
        lot_number: '', 
        invoice_number: '', 
        tracking_number: '', 
        carrier: '', 
        winning_bid: 0, 
        buyer_premium: 0, 
        total_taxes: 0, 
        total_freight: 0, 
        items: [{ item_name: '', item_no: '', quantity: null, unit_cost: 0, description: '' }]
    };

    this.showAuctionPopup = true;

    // ✅ Wait for Vue to render changes
    await this.$nextTick();

    // ✅ Ensure Electron has focused the window before forcing input focus
    requestAnimationFrame(() => {
        setTimeout(() => {
            const platformField = document.querySelector(".popup input");
            if (platformField) {
                console.log("🎯 Setting focus to Platform field...");
                platformField.focus();
                platformField.select();
            } else {
                console.warn("⚠️ Platform field not found, retrying...");
            }
        }, 50); // ✅ Small delay ensures rendering
    });

    console.log("✅ Popup opened & Platform field focus attempt made.");
},


  // ✅ Auto-populates Item No & Description when user selects an item name
// ✅ Works for both new auctions and adding lines to existing auctions
async fetchItemSuggestions(index = null) {
    let searchTerm;

    if (index !== null) {
        // 🆕 New Auction Mode (Selecting an item from newAuction.items)
        searchTerm = this.newAuction.items[index]?.item_name;
    } else {
        // ✏️ Add Line Mode (Selecting an item from newLine)
        searchTerm = this.newLine.item_name;
    }

    if (!searchTerm || searchTerm.length < 2) {
        console.warn("⚠ Search term is too short or missing:", searchTerm);
        return; // ✅ Prevent unnecessary API calls
    }

    console.log(`🔎 Fetching item suggestions for: ${searchTerm}`);

    try {
        const response = await api.get(`/items/search?query=${searchTerm}`);
        console.log(`📥 API returned:`, response.data);

        this.itemSuggestions = response.data; // ✅ Update Vue UI
        console.log(`✅ Updated itemSuggestions:`, this.itemSuggestions);

    } catch (error) {
        console.error("❌ Error fetching item suggestions:", error);
    }
},


// ✅ Works for both new auctions and adding lines to existing auctions
async autoFillItemDetails(index = null) {
    let targetItem = index !== null ? this.newAuction.items[index] : this.newLine;

    console.log("🔍 Checking itemSuggestions:", this.itemSuggestions);
    console.log("🔍 Looking for item:", targetItem.item_name);

    if (!targetItem.item_name) {
        console.warn("⚠ No item name entered.");
        return;
    }

    // Ensure case-insensitive, whitespace-trimmed comparison
    const selectedItem = this.itemSuggestions.find(item => 
        item.item_name.trim().toLowerCase() === targetItem.item_name.trim().toLowerCase()
    );

    if (selectedItem) {
        console.log("✅ Found existing item:", selectedItem);
        targetItem.item_no = selectedItem.item_no;
        targetItem.description = selectedItem.description || "No description provided"; 
    } else {
        console.log("❓ Item not found, asking user if they want to add it.");
        const confirmAdd = confirm("This item does not exist. Would you like to add it?");
        if (confirmAdd) {
            this.openNewItemPopup(index);
        } else {
            targetItem.item_name = "";
        }
    }
},

openNewItemPopup(index) {
    console.log("🚀 Opening New Item Popup for row:", index);

    this.currentItemIndex = index;  // ✅ Store the row index
    this.newItemDetails = { 
        item_name: this.newAuction.items[index].item_name, 
        description: "",
        wine_type: "",
        service_class: "",
        vintage: "",
        bottle_size: "750ml",
        chateau: "",
        region: "",
        country: "",
        category_id: ""
    };
    this.showNewItemModal = true;  // ✅ Show the modal

    // ✅ Wait for Vue to render the modal, then focus the input field
    this.$nextTick(() => {
        if (this.$refs.newItemDescriptionInput) {
            this.$refs.newItemDescriptionInput.focus();
            console.log("🎯 Cursor moved to description field");
        } else {
            console.error("❌ Could not focus on description input");
        }
    });
},


    // ✅ Removes a row when clicking the delete button
    removeLineItem(index) {
      this.newAuction.items.splice(index, 1);
    },

    
  addLineItem() {
    this.newAuction.items.push({
      item_name: '',
      item_no: '',
      description: '',
      quantity: 1,
      unit_cost: 0
    });
  },

   calculateFreight(item) {
    const totalQuantity = this.newAuction.items.reduce((sum, i) => sum + i.quantity, 0);
    return totalQuantity > 0 ? ((item.quantity / totalQuantity) * this.newAuction.total_freight).toFixed(2) : "0.00";
  },

  calculateTaxes(item) {
    const totalCost = this.newAuction.items.reduce((sum, i) => sum + i.cost, 0);
    return totalCost > 0 ? ((item.cost / totalCost) * this.newAuction.total_taxes).toFixed(2) : "0.00";
  },

  async createAuction() {
    try {
        console.log("📤 Sending Auction Data:", JSON.stringify(this.newAuction, null, 2));

        // Ensure `auction_id` is set before sending
        if (!this.newAuction.auction_id) {
            this.newAuction.auction_id = await this.fetchNextAuctionId();
        }

        this.newAuction.items = this.newAuction.items.map(item => ({
            ...item,
            quantity: Number(item.quantity || 0),
            unit_cost: Number(item.unit_cost || 0)
        }));

        this.newAuction.winning_bid = this.newAuction.items.reduce((sum, item) => {
            return sum + Number(item.quantity || 0) * Number(item.unit_cost || 0);
        }, 0);

        console.log("📦 Final purchase payload with unit costs:", JSON.stringify(this.newAuction, null, 2));

        await api.post('/auctions', this.newAuction);

        console.log("✅ Auction saved successfully!");

        // ✅ **Force Focus on Platform Before Closing**
        this.$nextTick(() => {
            const platformField = document.querySelector(".popup input"); 
            if (platformField) {
                console.log("🎯 Setting focus to Platform field before closing...");
                platformField.focus();
                platformField.select();
            }
        });

        // ✅ **Reset Form Immediately After Saving**
        this.newAuction = { 
            auction_id: null,
            platform: '', 
            auction_date: '', 
            lot_number: '', 
            invoice_number: '', 
            tracking_number: '', 
            carrier: '', 
            winning_bid: 0, 
            buyer_premium: 0, 
            total_taxes: 0, 
            total_freight: 0, 
            items: [{ item_name: '', item_no: '', quantity: null, description: '' }]
        };

        // ✅ **Scroll to Top Before Closing**
        setTimeout(() => {
            const popupContainer = document.querySelector(".popup");
            if (popupContainer) {
                popupContainer.scrollTop = 0;
            }
        }, 50);

        // ✅ **Now Close Popup**
        setTimeout(() => {
            this.showAuctionPopup = false;
            console.log("✅ Auction popup closed!");
        }, 100);

        this.fetchAuctions(); // ✅ Refresh auction list
    } catch (error) {
        console.error("❌ Error saving auction:", error);
        alert("Error saving auction!");
    }
},


resetAuctionForm() {
    this.newAuction = { 
        auction_id: null,  // ✅ Reset ID so the next one is fetched properly
        platform: '', 
        auction_date: '', 
        lot_number: '', 
        invoice_number: '', 
        tracking_number: '', 
        carrier: '', 
        winning_bid: 0, 
        buyer_premium: 0, 
        total_taxes: 0, 
        total_freight: 0, 
        items: [{ item_name: '', item_no: '', quantity: null, unit_cost: 0, description: '' }]
    };

    console.log("🔄 Form reset before closing. Now focusing on Platform field...");

    // ✅ Force focus on Platform field
    this.$nextTick(() => {
        const inputField = document.querySelector(".popup input");
        if (inputField) {
            inputField.focus();
            inputField.select();
            console.log("✅ Platform field focused successfully!");
        } else {
            console.error("❌ Platform field not found after form reset.");
        }
    });
},


calculateTotalCost() {
    this.totalCost =
        Number(this.newLine.quantity || 0) *
        Number(this.newLine.unit_cost || 0);
},

        async openAddLinePopup(auction) {
    this.auctionToEdit = auction;

    console.log(`🔍 Fetching received status for Auction: ${auction.auction_id}`);

    try {
        const response = await api.get(`/auction_lines/check_received/${auction.auction_id}`);
        console.log(`📥 API Response:`, response.data);

        this.received_status = response.data.received; // ✅ Store the received status

        if (this.received_status === 'Y') {  // ✅ Block if received status is 'Y'
            alert("❌ Cannot add items to a received auction.");
            return;
        }

        this.showAddLinePopup = true;
        this.newLine = {
            item_no: "",
            item_name: "",
            description: "",
            quantity: null,
            unit_cost: 0
        };

    } catch (error) {
        console.error("❌ Error fetching auction received status:", error);
        alert("⚠️ Failed to check auction status.");
    }
},


        closeAddLinePopup() {
            this.showAddLinePopup = false;
        },

        async addAuctionLine() {
            if (!this.auctionToEdit || !this.newLine.item_no || !this.newLine.item_name || !this.newLine.quantity) {
                alert("Please fill out all required fields.");
                return;
            }

            try {
                const auctionLineData = {
                    auction_id: this.auctionToEdit.auction_id,
                    item_no: this.newLine.item_no,
                    item_name: this.newLine.item_name,
                    description: this.newLine.description || "",
                    quantity: parseInt(this.newLine.quantity, 10),
                    unit_cost: Number(this.newLine.unit_cost || 0)
                };

                console.log("📤 Sending auction line data:", auctionLineData);

                await api.post("/auction_lines", auctionLineData);

                alert("✅ Auction line added successfully!");

                this.fetchAuctionLines(this.auctionToEdit.auction_id); // Refresh UI
                this.closeAddLinePopup();
            } catch (error) {
                console.error("❌ Error adding auction line:", error);
                alert("Error adding auction line. Check console for details.");
            }
        },

  // ✅ Open Edit Auction Popup
  async editAuction(auction) {
    console.log("🛠 Edit button clicked for auction:", auction);

    try {
        // ✅ Step 1: Check the auction header
        if (auction.received_status === 'Y') {
            alert("🚫 Please unreceive before editing this auction."); 
            return; // ❌ Stop execution
        }

        // ✅ Step 2: Fetch auction lines to check for received items
        const response = await api.get(`/auction_lines/${auction.auction_id}`);
        const auctionLines = response.data;

        // ✅ Step 3: Check if ANY line is received
        const anyReceived = auctionLines.some(line => line.received_flag === 'Y');

        if (anyReceived) {
            alert("🚫 This auction contains received items. Please unreceive all items before editing.");
            return; // ❌ Stop execution
        }

        // ✅ Step 4: Proceed if no received lines
        this.showEditPopup = true;

    } catch (error) {
        console.error("❌ Error checking auction lines:", error);
        alert("❌ Failed to check auction lines. Please try again.");
    }

    // Format the auction date for the input field
    const formattedDate = auction.auction_date
      ? new Date(auction.auction_date).toISOString().split("T")[0]
      : "";

    // Ensure all values are properly assigned
    this.editAuctionData = {
      auction_id: auction.auction_id,
      platform: auction.platform || "", 
      auction_date: formattedDate,
      lot_number: auction.lot_number || "",
      invoice_number: auction.invoice_number || "",
      tracking_number: auction.tracking_number || "",
      carrier: auction.carrier || "",
      winning_bid: auction.winning_bid !== null && auction.winning_bid !== undefined ? auction.winning_bid : "",  // 🛠 Preserve actual value
      buyer_premium: auction.buyer_premium !== null && auction.buyer_premium !== undefined ? auction.buyer_premium : "",  // 🛠 Preserve actual value
      total_taxes: auction.total_taxes !== null && auction.total_taxes !== undefined ? auction.total_taxes : 0,
      total_freight: auction.total_freight !== null && auction.total_freight !== undefined ? auction.total_freight : 0,
      total_cost: auction.total_cost !== null && auction.total_cost !== undefined ? auction.total_cost : 0
    };

    console.log("🎯 Updated editAuctionData:", this.editAuctionData);
},




  // ✅ Update Auction Header
  async updateAuction() {
    try {
        console.log("💾 Saving auction:", this.editAuctionData);

        await api.put(`/auctions/${this.editAuctionData.auction_id}`, this.editAuctionData);
        
        this.showEditPopup = false;
        this.fetchAuctions(); // ✅ Refresh auction list after updating

    } catch (error) {
        console.error("❌ Error updating auction:", error);
        
        if (error.response && error.response.status === 400) {
            alert(error.response.data.error); // 🔥 Show "Unreceivify before you Edit" message
        } else {
            alert("An error occurred while updating the auction.");
        }
    }
},
  
  
// ✅ Open Edit Line Popup (Now Async)
async editLine(line) {
    console.log("🛠 Edit button clicked for line:", line);
    try {
        // ✅ Fetch all auction lines for this auction
        const response = await api.get(`/auction_lines/${line.auction_id}`);
        const auctionLines = response.data;

        console.log(`📥 API Response for Auction Lines:`, auctionLines);

        // ✅ Check if ANY line has been received
        const anyReceived = auctionLines.some(l => l.received_flag === 'Y');

        if (anyReceived) {
            console.warn("🚫 Cannot edit any lines in this auction because at least one has been received!");
            alert("🚫 This auction has received items. No lines can be edited.");
            return; // ⛔ Stop execution, do not open the popup
        }
    } catch (error) {
        console.error("❌ Error fetching auction lines:", error);
        alert("⚠️ Error checking auction lines. Please try again.");
        return;
    }

    // ✅ If no lines are received, allow editing
    this.showEditLinePopup = true;

    // ✅ Copy the selected line
    this.editLineData = {
        auction_detail_id: line.auction_detail_id,
        auction_id: line.auction_id,
        item_name: line.item_name,
        tracking_no_line: line.tracking_no_line,
        carrier_line: line.carrier_line,
        item_no: line.item_no,
        quantity: line.quantity,
        unit_cost: line.unit_cost || 0,
        allocated_cost: line.allocated_cost,
        allocated_premium: line.allocated_premium,
        allocated_taxes: line.allocated_taxes,
        allocated_freight: line.allocated_freight,
        landed_unit_cost: line.landed_unit_cost,
        received_flag: line.received_flag
    };

    console.log("📌 editLineData now contains:", this.editLineData);

    // ✅ Fetch auction header data BEFORE opening popup
    try {
        console.log(`🔍 Fetching auction header for auction_id: ${line.auction_id}`);
        const response = await api.get(`/auction/${line.auction_id}`);
        this.auctionHeaderData = response.data;
        console.log("📌 auctionHeaderData now contains:", this.auctionHeaderData);
    } catch (error) {
        console.error("❌ Error fetching auction header data:", error);
        return;  // Don't open the popup if data fails
    }

    // ✅ Fetch all auction lines for total quantity calculation
    try {
        console.log(`🔍 Fetching auction lines for auction_id: ${line.auction_id}`);
        const linesResponse = await api.get(`/auction_lines/${line.auction_id}`);
        const auctionLines = linesResponse.data;

        this.totalAuctionQuantity = auctionLines.reduce((sum, l) => sum + (l.quantity || 0), 0);  // ✅ Calculate total quantity
        console.log("📊 Total auction quantity after fetching lines:", this.totalAuctionQuantity);
    } catch (error) {
        console.error("❌ Error fetching auction lines:", error);
        this.totalAuctionQuantity = 1;  // Fallback to prevent division by zero
    }
},



  // ✅ Update Auction Line Quantity
  async updateAuctionLine() {
    try {
        console.log("💾 Updating auction line:", this.editLineData);
        
        // Ensure auction_id is present
        if (!this.editLineData.auction_id) {
            console.error("❌ ERROR: Missing auction_id before API call!");
            return;
        }

        // ✅ Ensure `tracking_no_line` and `carrier_line` are included
        await api.put(`/auction_lines/${this.editLineData.auction_detail_id}`, {
            quantity: this.editLineData.quantity,
            unit_cost: Number(this.editLineData.unit_cost || 0),
            tracking_no_line: this.editLineData.tracking_no_line || "",
            carrier_line: this.editLineData.carrier_line || ""
        });

        console.log("✅ Auction line updated successfully");

        this.showEditLinePopup = false;

        console.log("🔍 Fetching auction lines for auction_id:", this.editLineData.auction_id);
        await this.fetchAuctionLines(this.editLineData.auction_id);

    } catch (error) {
        console.error("❌ Error updating auction line:", error);
    }
},

async focusAuctionName() {
    await this.$nextTick();

    console.log("🔍 Checking if Add Auction popup is visible...");

    if (!this.showAuctionPopup) {
        console.warn("⚠️ Popup is not visible yet, skipping focus.");
        return;
    }

    setTimeout(() => {  
        const popupContainer = document.querySelector(".popup");
        console.log("📌 Popup container found:", popupContainer ? "Yes" : "No");

        if (!popupContainer) {
            console.error("❌ Popup container not found!");
            return;
        }

        const inputField = popupContainer.querySelector("input");
        console.log("📌 First input field found:", inputField ? "Yes" : "No");

        if (inputField) {
            console.log("🎯 Focusing on Platform field...");
            
            // **🔥 TRICK: Simulate window focus change**
            window.blur();  // Temporarily blur the window
            setTimeout(() => {
                window.focus(); // Then refocus it
                inputField.focus();
                inputField.select();
            }, 100); // 🔥 Small delay ensures it works
        } else {
            console.warn("⚠️ No input field found in popup.");
        }
    }, 500); // 🔥 500ms delay for Vue/Electron to process
},

async saveNewItem() {
    try {
        if (!this.newItemDetails.item_name || !this.newItemDetails.wine_type || !this.newItemDetails.service_class) {
            alert("Item Name, Wine Type, and Class are required.");
            return;
        }

        const response = await api.post('/items', this.newItemDetails);

        if (response.data && response.data.item_no) {
            console.log("✅ New item saved:", response.data);

            this.newAuction.items[this.currentItemIndex].item_no = response.data.item_no;
            this.newAuction.items[this.currentItemIndex].description =
                response.data.description || response.data.details || "";

            this.showNewItemModal = false;
        } else {
            console.error("❌ Backend did not return an item_no!");
        }
    } catch (error) {
        console.error("❌ Error adding new item:", error);
        console.error("Backend response:", error.response?.data);
    }
},

async deleteAuction(auctionId) {
    try {
        console.log(`🗑 Checking if auction ${auctionId} is received before deleting...`);

        // ✅ Fetch received status
        const checkResponse = await api.get(`/auction_lines/check_received/${auctionId}`);

        // ✅ Debug Response
        console.log(`📥 API Response for received status:`, checkResponse.data);

        if (checkResponse.data.received === "Y") {
            alert("🚨 This auction is partially or fully received. Unreceive all items before deleting.");
            return;
        }


        // ✅ Confirmation popup
        if (!confirm("Are you sure you want to delete this auction? This action cannot be undone.")) {
            return; // 🚨 Stop if user cancels
        }

        console.log(`🗑 Deleting auction with ID: ${auctionId}`);

        await api.delete(`/auctions/${auctionId}`);

        console.log("✅ Auction deleted successfully");

        this.fetchAuctions(); // 🔄 Refresh list after deletion
    } catch (error) {
        console.error("❌ Error deleting auction:", error);
        alert("Error deleting auction.");
    }
},


  
  // ✅ Step 1: Check if ANY line in the auction is received before allowing deletion
async confirmDeleteLine(line) {
    try {
        // ✅ Fetch all auction lines for this auction
        const response = await api.get(`/auction_lines/${line.auction_id}`);
        const auctionLines = response.data;

        console.log(`📥 API Response for Auction Lines:`, auctionLines);

        // ✅ Check if ANY line has been received
        const anyReceived = auctionLines.some(l => l.received_flag === 'Y');

        if (anyReceived) {
            console.warn("🚫 Cannot delete any lines in this auction because at least one has been received!");
            alert("🚫 This auction has received items. No lines can be deleted.");
            return; // ⛔ Stop execution
        }
    } catch (error) {
        console.error("❌ Error fetching auction lines:", error);
        alert("⚠️ Error checking auction lines. Please try again.");
        return;
    }

    // ✅ If no lines are received, allow deletion
    this.selectedLineToDelete = line.auction_detail_id;
    this.showDeletePopup = true; // Show confirmation popup
},


  // ✅ Step 2: Call backend DELETE API
  async deleteLineConfirmed() {
    try {
        console.log(`🗑 Deleting auction line: ${this.selectedLineToDelete}`);

        await api.delete(`/auction_lines/${this.selectedLineToDelete}`);

        console.log("✅ Line deleted successfully");

        // ✅ Hide popup
        this.showDeletePopup = false;

        // ✅ Remove the deleted line from auctionLines in the UI
        for (const auctionId in this.auctionLines) {
            this.auctionLines[auctionId] = this.auctionLines[auctionId].filter(
                line => line.auction_detail_id !== this.selectedLineToDelete
            );
        }

        console.log("✅ UI updated: auction line removed");
        
    } catch (error) {
        console.error("❌ Error deleting line:", error);
        alert("Failed to delete auction line.");
    }
},


async unReceiveAuction(auction) {
  try {
    console.log(`🔄 Checking auction lines for auction ID: ${auction.auction_id} before unreceiving...`);

    // ✅ Fetch latest auction lines directly from the backend
    const response = await api.get(`/auction_lines/${auction.auction_id}`);
    const auctionLines = response.data;

    // ✅ Check if any line is already unreceived ('N')
    const anyUnreceived = auctionLines.some(line => line.received_flag === 'N');

    if (anyUnreceived) {
      alert("❌ Auction partially unreceived. You may only unreceive at the line level now.");
      return;
    }

    // ✅ Grab receipt_date from any line (they're all received and share the same date)
    const receiptDate = auctionLines[0]?.receipt_date;
    if (!receiptDate) {
      alert("❌ Cannot unreceive. Missing receipt date.");
      return;
    }

    // ✅ Step 1: Check for ship conflicts using current receipt date
    try {
      const conflictCheck = await api.get(`/check-ship-conflicts/${auction.auction_id}`, {
        params: {
          receipt_date: receiptDate
        }
      });

      if (conflictCheck.data.conflicts.length > 0) {
        const details = conflictCheck.data.conflicts
          .map(c => `• Item ${c.item_no} in Sale #${c.sale_id}`)
          .join('\n');

        alert(`❌ Cannot unreceive:\nOne or more items in this auction have already been shipped after the recorded receipt date.\n\n${details}`);
        console.warn("🚨 Ship Conflicts Detected:", conflictCheck.data.conflicts);
        return;
      }
    } catch (conflictError) {
      console.error("❌ Error checking for ship conflicts before unreceiving:", conflictError);
      alert("❌ Failed to check for shipment conflicts. Cannot proceed.");
      return;
    }

    // ✅ Confirm before unreceiving
    if (!confirm(`Are you sure you want to unreceive auction ${auction.auction_id}? This will reverse all inventory adjustments.`)) {
      return;
    }

    console.log(`🔄 Sending UNRECEIVE request for auction ID: ${auction.auction_id}`);

    const unreceiveResponse = await api.put(`/unreceive/${auction.auction_id}`);

    console.log("✅ Auction unreceived successfully:", unreceiveResponse.data);
    alert("Auction unreceived successfully!");

    // ✅ Log the updated `received_status` BEFORE refreshing auctions
    console.log(`🔄 BEFORE fetchAuctions: auction.received_status = ${auction.received_status}`);

    // ✅ Refresh the auction list
    await this.fetchAuctions();

    // ✅ Log the updated `received_status` AFTER refreshing auctions
    const updatedAuction = this.auctions.find(a => a.auction_id === auction.auction_id);
    if (updatedAuction) {
      console.log(`🔄 AFTER fetchAuctions: auction.received_status = ${updatedAuction.received_status}`);
    } else {
      console.log("❌ Auction not found in refreshed data!");
    }

  } catch (error) {
    console.error("❌ Error unreceiving auction:", error);

    if (error.response) {
      console.log("🔍 Full Axios Error Response:", error.response);
      const errorMessage = error.response.data.error || "Unknown error from server.";
      alert(`❌ Unreceive failed: ${errorMessage}`);
    } else {
      alert("❌ Unreceive failed: No response from server.");
    }
  }
},

    toggleExpand(auctionId) {
      if (this.expandedAuctions.includes(auctionId)) {
        this.expandedAuctions = this.expandedAuctions.filter(id => id !== auctionId);
      } else {
        this.expandedAuctions.push(auctionId);
        this.fetchAuctionLines(auctionId);
      }
    },
    
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toISOString().split('T')[0];
    },


    async fetchAuctionLines(auctionId) {
  try {
    console.log(`🔍 Fetching auction lines for auction ${auctionId}...`);

    const response = await api.get(`/auction_lines/${auctionId}`);
    console.log("📥 API Response for Auction Lines:", response.data); // ✅ Check if received_flag is present

    // ✅ Check if received_flag exists
    response.data.forEach(line => {
      console.log(`DEBUG: auction_detail_id=${line.auction_detail_id}, received_flag=${line.received_flag}`);
    });

    // ✅ Correct way to update the reactive object in Vue 3
    this.auctionLines = { ...this.auctionLines, [auctionId]: response.data };

  } catch (error) {
    console.error(`❌ Error fetching auction lines for auction ${auctionId}:`, error);
    this.auctionLines = { ...this.auctionLines, [auctionId]: [] };
  }
},



    openTrackingLink(trackingNumber, carrier) {
      if (!trackingNumber || !carrier) {
        alert("❌ Tracking URL could not be determined. Missing Tracking Number or Carrier.");
        return;
      }
      const url = this.getTrackingURL(trackingNumber, carrier);
      if (url !== '#') {
        if (window.electron) {
          window.electron.openExternal(url);
        } else {
          window.open(url, '_blank');
        }
      } else {
        alert("❌ Invalid Carrier or Tracking Number!");
      }
    },
    getTrackingURL(trackingNumber, carrier) {
      const carrierNormalized = carrier.trim().toUpperCase();
      const carrierUrls = {
        'UPS': `https://www.ups.com/track?tracknum=${trackingNumber}`,
        'FEDEX': `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`,
        'USPS': `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`,
        'DHL': `https://www.dhl.com/global-en/home/tracking.html?tracking-id=${trackingNumber}`
      };
      return carrierUrls[carrierNormalized] || '#';
    }
  }
};
</script>


<style scoped>
/* =========================================================
   LotLedger Wine Bar - Purchases Page
   Dark Portland wine-bar / rock-scene redesign
   ========================================================= */

/* ---------- Page Shell ---------- */

.purchases-shell {
  min-height: 100vh;
  padding: 28px;
  font-family: "Inter", "Poppins", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.38), transparent 34%),
    radial-gradient(circle at bottom right, rgba(194, 74, 47, 0.18), transparent 32%),
    linear-gradient(135deg, #0d090d 0%, #171016 42%, #24141b 100%);
  color: #f7efe8;
}

/* ---------- Hero ---------- */

.purchase-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 22px;
  padding: 30px;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.035));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.38);
  backdrop-filter: blur(18px);
}

.hero-copy-wrap {
  max-width: 760px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #c8a66a;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 12px;
  font-weight: 900;
}

.purchase-hero h1 {
  margin: 0;
  font-size: clamp(40px, 7vw, 72px);
  line-height: 0.92;
  letter-spacing: -0.07em;
}

.hero-copy {
  max-width: 620px;
  margin: 16px 0 0;
  color: #cfc2bd;
  font-size: 16px;
  line-height: 1.5;
}

/* ---------- Buttons ---------- */

.primary-action,
.secondary-action,
.cancel-action,
.receive-action,
.warning-action,
.danger-action,
.ghost-action,
.modal-button {
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: 0;
  color: #fff8f1;
  font-weight: 900;
  font-size: 14px;
  cursor: pointer;
  transition:
    transform 0.14s ease,
    box-shadow 0.14s ease,
    border-color 0.14s ease,
    background 0.14s ease;
  touch-action: manipulation;
}

.primary-action {
  min-height: 58px;
  padding: 0 26px;
  background: linear-gradient(135deg, #8f123f, #c24a2f);
  box-shadow: 0 14px 32px rgba(194, 74, 47, 0.3);
}

.primary-action.small {
  min-height: 46px;
  padding: 0 18px;
}

.secondary-action {
  background: rgba(200, 166, 106, 0.18);
  border: 1px solid rgba(200, 166, 106, 0.28);
  color: #f1c979;
}

.cancel-action,
.ghost-action {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.11);
}

.receive-action {
  background: rgba(47, 170, 118, 0.24);
  border: 1px solid rgba(118, 224, 170, 0.28);
  color: #9df0bd;
}

.warning-action {
  background: rgba(200, 166, 106, 0.22);
  border: 1px solid rgba(200, 166, 106, 0.3);
  color: #f1c979;
}

.danger-action {
  background: rgba(155, 45, 45, 0.38);
  border: 1px solid rgba(255, 118, 118, 0.25);
  color: #ffb4a8;
}

.primary-action:hover,
.secondary-action:hover,
.cancel-action:hover,
.receive-action:hover,
.warning-action:hover,
.danger-action:hover,
.ghost-action:hover,
.modal-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.24);
}

.primary-action:active,
.secondary-action:active,
.cancel-action:active,
.receive-action:active,
.warning-action:active,
.danger-action:active,
.ghost-action:active,
.modal-button:active {
  transform: translateY(1px) scale(0.99);
}

/* ---------- Toolbar ---------- */

.purchase-toolbar {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 230px 180px;
  gap: 14px;
  margin: 22px 0;
}

.search-wrap,
.toolbar-select {
  min-height: 56px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  background: rgba(12, 8, 12, 0.7);
  color: #f7efe8;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.03);
}

.search-wrap {
  display: flex;
  align-items: center;
  padding: 0 16px;
}

.search-icon {
  color: #c8a66a;
  font-size: 24px;
  margin-right: 10px;
  line-height: 1;
}

.search-wrap input {
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  color: #f7efe8;
  font-size: 16px;
}

.search-wrap input::placeholder {
  color: #8f827e;
}

.toolbar-select {
  padding: 0 16px;
  font-size: 15px;
  outline: 0;
}

/* ---------- Purchase Cards ---------- */

.purchase-grid {
  display: grid;
  gap: 18px;
}

.purchase-card {
  border-radius: 28px;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.085), rgba(255, 255, 255, 0.035));
  border: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.3);
}

.purchase-card.received {
  border-color: rgba(118, 224, 170, 0.18);
}

.purchase-card-main {
  padding: 24px;
  cursor: pointer;
}

.purchase-title-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.label {
  margin: 0 0 5px;
  color: #9b8f8b;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 900;
}

.purchase-title-row h2 {
  margin: 0;
  font-size: clamp(24px, 4vw, 34px);
  letter-spacing: -0.045em;
  line-height: 1.02;
}

.status-pill,
.mini-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 900;
  white-space: nowrap;
}

.status-open {
  background: rgba(200, 166, 106, 0.16);
  color: #f1c979;
  border: 1px solid rgba(200, 166, 106, 0.28);
}

.status-received {
  background: rgba(47, 170, 118, 0.14);
  color: #76e0aa;
  border: 1px solid rgba(118, 224, 170, 0.28);
}

.purchase-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 22px;
}

.purchase-metrics div {
  min-height: 78px;
  padding: 15px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.purchase-metrics span {
  display: block;
  color: #978b86;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 900;
}

.purchase-metrics strong {
  display: block;
  margin-top: 8px;
  font-size: 20px;
  color: #fff8f1;
}

.tracking-row {
  margin-top: 14px;
}

.text-link {
  padding: 0;
  border: 0;
  background: transparent;
  color: #c8a66a;
  font-weight: 900;
  cursor: pointer;
  text-align: left;
}

.text-link.compact {
  margin-top: 8px;
  font-size: 13px;
}

.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0 24px 24px;
}

/* ---------- Expanded Lines ---------- */

.purchase-lines {
  padding: 24px;
  background: rgba(0, 0, 0, 0.23);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.line-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.line-header h3 {
  margin: 0;
  font-size: 22px;
  letter-spacing: -0.03em;
}

.tracking-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #cfc2bd;
  font-size: 14px;
  font-weight: 700;
}

.tracking-toggle input {
  width: 18px;
  height: 18px;
  accent-color: #8f123f;
}

.line-list {
  display: grid;
  gap: 10px;
}

.line-card {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) auto auto auto;
  align-items: center;
  gap: 14px;
  padding: 15px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.07);
}

.line-main h4 {
  margin: 0;
  font-size: 17px;
  color: #fff8f1;
}

.line-main p {
  margin: 5px 0 0;
  color: #aaa09b;
  font-size: 14px;
}

.line-meta {
  display: flex;
  gap: 12px;
  color: #e9d9cf;
  font-weight: 900;
  white-space: nowrap;
}

.line-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.line-actions button {
  min-height: 42px;
  padding: 0 13px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  background: rgba(255, 255, 255, 0.07);
  color: #f7efe8;
  font-weight: 900;
  cursor: pointer;
}

.line-actions .danger-text {
  color: #ffb4a8;
  border-color: rgba(255, 118, 118, 0.22);
}

.empty-state,
.empty-lines {
  padding: 38px;
  text-align: center;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px dashed rgba(255, 255, 255, 0.16);
  color: #cfc2bd;
}

.empty-state h2,
.empty-lines h4 {
  margin: 0 0 8px;
  color: #fff8f1;
  letter-spacing: -0.035em;
}

.empty-state p,
.empty-lines p {
  margin: 0 0 18px;
}

/* ---------- Transitions ---------- */

.expand-enter-active,
.expand-leave-active {
  transition: all 0.22s ease;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* =========================================================
   Modals / Popups
   ========================================================= */

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 26px;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.34), transparent 36%),
    rgba(5, 3, 5, 0.78);
  backdrop-filter: blur(12px);
}

.modal-overlay.nested {
  z-index: 2200;
}

.purchase-modal {
  width: min(920px, 96vw);
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 30px;
  background:
    linear-gradient(145deg, rgba(35, 22, 29, 0.98), rgba(14, 10, 14, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.11);
  color: #f7efe8;
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.55);
}

.wide-modal {
  width: min(1120px, 96vw);
}

.small-modal {
  width: min(560px, 94vw);
}

.modal-topline {
  height: 5px;
  flex: 0 0 auto;
  background: linear-gradient(90deg, #8f123f, #c24a2f, #c8a66a);
}

.modal-topline.gold {
  background: linear-gradient(90deg, #c8a66a, #8f123f);
}

.modal-topline.green {
  background: linear-gradient(90deg, #2faa76, #76e0aa);
}

.modal-topline.amber {
  background: linear-gradient(90deg, #c8a66a, #d77a2d);
}

.modal-topline.red {
  background: linear-gradient(90deg, #9b2d2d, #ff765f);
}

.modal-header {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  padding: 26px 28px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-header h2 {
  margin: 0;
  font-size: clamp(28px, 5vw, 44px);
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.modal-header p {
  margin: 10px 0 0;
  color: #cfc2bd;
  line-height: 1.45;
}

.icon-close {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.07);
  color: #fff8f1;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
}

.modal-body {
  overflow-y: auto;
  padding: 22px 28px 28px;
}

.form-panel {
  padding: 20px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 16px;
}

.panel-heading {
  margin-bottom: 16px;
}

.panel-heading h3 {
  margin: 0;
  font-size: 20px;
  letter-spacing: -0.025em;
}

.panel-heading p {
  margin: 6px 0 0;
  color: #aaa09b;
  font-size: 14px;
}

.split-heading {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
}

/* ---------- Forms ---------- */

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 14px;
}

.form-grid.four-col {
  grid-template-columns: repeat(4, minmax(140px, 1fr));
}

.total-field {
  grid-column: span 2;
}

.purchase-modal label {
  display: grid;
  gap: 7px;
  color: #f7efe8;
}

.purchase-modal label span {
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.purchase-modal input,
.purchase-modal select,
.purchase-modal textarea {
  min-height: 52px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  outline: 0;
  background: rgba(6, 4, 6, 0.58);
  color: #fff8f1;
  padding: 0 14px;
  font-size: 15px;
  font-family: inherit;
}

.purchase-modal textarea {
  min-height: 110px;
  padding-top: 14px;
  resize: vertical;
}

.purchase-modal input::placeholder,
.purchase-modal textarea::placeholder {
  color: #766c68;
}

.purchase-modal input:focus,
.purchase-modal select:focus,
.purchase-modal textarea:focus {
  border-color: rgba(200, 166, 106, 0.65);
  box-shadow: 0 0 0 3px rgba(200, 166, 106, 0.14);
}

.disabled-field,
.purchase-modal input:disabled,
.purchase-modal select:disabled,
.purchase-modal textarea:disabled {
  background: rgba(255, 255, 255, 0.045);
  color: #8f827e;
  cursor: not-allowed;
}

/* ---------- Touch Table ---------- */

.touch-table {
  display: grid;
  gap: 8px;
}

.touch-table-head,
.touch-table-row {
  display: grid;
  grid-template-columns: minmax(220px, 1.4fr) minmax(110px, 0.7fr) minmax(90px, 0.5fr) minmax(180px, 1fr) 48px;
  gap: 10px;
  align-items: center;
}

.touch-table-head {
  color: #9b8f8b;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  padding: 0 6px;
}

.touch-table-row {
  padding: 10px;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.row-delete {
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid rgba(255, 118, 118, 0.24);
  background: rgba(155, 45, 45, 0.3);
  color: #ffb4a8;
  font-size: 25px;
  line-height: 1;
  font-weight: 900;
  cursor: pointer;
}

/* ---------- Modal Footer ---------- */

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 4px;
}

.modal-actions .primary-action,
.modal-actions .cancel-action,
.modal-actions .receive-action,
.modal-actions .warning-action,
.modal-actions .danger-action,
.modal-actions .modal-button {
  min-width: 140px;
}

/* ---------- Scrollbar ---------- */

.modal-body::-webkit-scrollbar {
  width: 10px;
}

.modal-body::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.04);
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgba(200, 166, 106, 0.32);
  border-radius: 999px;
}

/* =========================================================
   Responsive / Touch Layout
   ========================================================= */

@media (max-width: 1000px) {
  .purchase-toolbar {
    grid-template-columns: 1fr;
  }

  .purchase-metrics {
    grid-template-columns: repeat(2, 1fr);
  }

  .line-card {
    grid-template-columns: 1fr;
  }

  .line-actions {
    justify-content: flex-start;
  }

  .form-grid,
  .form-grid.four-col {
    grid-template-columns: 1fr;
  }

  .total-field {
    grid-column: span 1;
  }

  .touch-table-head {
    display: none;
  }

  .touch-table-row {
    grid-template-columns: 1fr;
  }

  .row-delete {
    width: 100%;
  }
}

@media (max-width: 720px) {
  .purchases-shell {
    padding: 16px;
  }

  .purchase-hero,
  .purchase-title-row,
  .line-header,
  .split-heading,
  .modal-header {
    flex-direction: column;
  }

  .purchase-hero {
    align-items: stretch;
    padding: 24px;
  }

  .primary-action,
  .secondary-action,
  .cancel-action,
  .receive-action,
  .warning-action,
  .danger-action,
  .ghost-action,
  .modal-button {
    width: 100%;
  }

  .purchase-metrics {
    grid-template-columns: 1fr;
  }

  .card-actions,
  .modal-actions {
    flex-direction: column;
  }

  .modal-overlay {
    padding: 12px;
    align-items: stretch;
  }

  .purchase-modal,
  .wide-modal,
  .small-modal {
    width: 100%;
    max-height: calc(100vh - 24px);
    border-radius: 24px;
  }

  .modal-header,
  .modal-body {
    padding-left: 20px;
    padding-right: 20px;
  }
}
</style>